import React, { useMemo } from 'react';
import { Box, Typography, Paper, Tooltip, Chip } from '@mui/material';
import { styled } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { getIconByName } from '../IconSelector';

const THEME_COLOR = '#009688';

// Mock icon/color mapping based on workflow type
export const WORKFLOW_TYPE_ICONS = {
  'new-request': { icon: 'badge', color: '#4CAF50' },
  'renew': { icon: 'flight_takeoff', color: '#2196F3' },
  'lost-id': { icon: 'description', color: '#FF9800' },
  'ohc': { icon: 'medical_services', color: '#E91E63' },
  'default': { icon: 'assignment', color: '#009688' }
};

// Get mock icon/color for a workflow
export const getWorkflowIconColor = (workflow) => {
  // First check if workflow has icon/color set (for future backend support)
  if (workflow.icon && workflow.color) {
    return { icon: workflow.icon, color: workflow.color };
  }

  // Otherwise, use type-based mock data
  const workflowType = workflow.type || workflow.workflowType || 'default';
  return WORKFLOW_TYPE_ICONS[workflowType] || WORKFLOW_TYPE_ICONS['default'];
};

// Extract steps/services from workflow definition
export const getWorkflowSteps = (workflow) => {
  try {
    // Handle different data structures
    let definition = workflow.definition;

    // If definition is a string, parse it
    if (typeof definition === 'string') {
      definition = JSON.parse(definition);
    }

    // Get steps from various locations
    const steps =
      workflow.steps ||
      (definition && definition.steps) ||
      (definition && definition.definition && definition.definition.steps) ||
      [];

    return Array.isArray(steps) ? steps : [];
  } catch (error) {
    console.warn('Error parsing workflow steps:', error);
    return [];
  }
};

// Estimate costs for workflow (mock calculation for UI)
export const estimateWorkflowCosts = (steps) => {
  if (!steps || steps.length === 0) {
    return { govtCharge: 0, serviceCharge: 0, total: 0 };
  }

  // Use consistent mock values per step (not random for stable UI)
  const govtPerStep = 30; // Average govt fee per step
  const servicePerStep = 50; // Average service charge per step

  const govtCharge = steps.length * govtPerStep;
  const serviceCharge = steps.length * servicePerStep;
  const total = govtCharge + serviceCharge;

  return { govtCharge, serviceCharge, total };
};

const TileContainer = styled(Paper)(({ theme, selected, tilecolor }) => ({
  padding: 14,
  cursor: 'pointer',
  borderRadius: 12,
  border: selected ? `2px solid ${THEME_COLOR}` : '2px solid transparent',
  backgroundColor: selected ? `${THEME_COLOR}08` : '#fff',
  boxShadow: selected
    ? `0 4px 12px ${THEME_COLOR}30`
    : '0 2px 8px rgba(0,0,0,0.06)',
  transition: 'all 0.2s ease',
  position: 'relative',
  overflow: 'hidden',
  minHeight: 140,
  display: 'flex',
  flexDirection: 'column',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: selected
      ? `0 6px 16px ${THEME_COLOR}40`
      : '0 4px 12px rgba(0,0,0,0.1)',
    borderColor: selected ? THEME_COLOR : `${tilecolor}50`,
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 4,
    backgroundColor: tilecolor || THEME_COLOR,
  },
}));

const InfoChip = styled(Box)(({ bgcolor }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  padding: '4px 10px',
  borderRadius: 12,
  backgroundColor: bgcolor || '#e0f2f1',
  fontSize: 11,
  fontWeight: 600,
}));

const IconBox = styled(Box)(({ bgcolor }) => ({
  width: 40,
  height: 40,
  borderRadius: 10,
  backgroundColor: bgcolor || THEME_COLOR,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  boxShadow: `0 2px 8px ${bgcolor}40`,
}));

const SelectionBadge = styled(Box)({
  position: 'absolute',
  top: 8,
  right: 8,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const WorkflowTile = ({
  workflow,
  selected = false,
  onClick,
}) => {
  const { icon, color } = getWorkflowIconColor(workflow);
  const IconComponent = getIconByName(icon);

  // Extract steps and calculate costs
  const steps = useMemo(() => getWorkflowSteps(workflow), [workflow]);
  const costs = useMemo(() => estimateWorkflowCosts(steps), [steps]);

  const handleClick = () => {
    onClick && onClick(workflow);
  };

  // Get workflow name - handle different data structures
  const workflowName = workflow.name || workflow.workflowName || 'Unnamed Workflow';
  const workflowDescription = workflow.description || '';

  // Get service names (first 3 for display)
  const serviceNames = useMemo(() => {
    return steps.slice(0, 3).map(step => step.name || step.serviceName || 'Service').join(', ');
  }, [steps]);

  const hasMoreServices = steps.length > 3;

  return (
    <TileContainer
      elevation={0}
      selected={selected}
      tilecolor={color}
      onClick={handleClick}
    >
      {/* Selection Badge */}
      {selected && (
        <SelectionBadge>
          <CheckCircleIcon sx={{ color: THEME_COLOR, fontSize: 22 }} />
        </SelectionBadge>
      )}

      {/* Header: Icon + Name */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1 }}>
        <IconBox bgcolor={color}>
          <IconComponent sx={{ fontSize: 22, color: '#fff' }} />
        </IconBox>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: 13,
              color: selected ? THEME_COLOR : '#212121',
              lineHeight: 1.3,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {workflowName}
          </Typography>
          {workflowDescription && (
            <Typography
              sx={{
                fontSize: 11,
                color: '#757575',
                lineHeight: 1.3,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {workflowDescription}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Services List */}
      {steps.length > 0 && (
        <Box sx={{ mb: 1, flex: 1 }}>
          <Typography
            sx={{
              fontSize: 10,
              color: '#9e9e9e',
              fontWeight: 500,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              mb: 0.5,
            }}
          >
            Services Included
          </Typography>
          <Tooltip
            title={steps.map(s => s.name || s.serviceName || 'Service').join(', ')}
            arrow
            placement="top"
          >
            <Typography
              sx={{
                fontSize: 11,
                color: '#616161',
                lineHeight: 1.4,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
              }}
            >
              {serviceNames}{hasMoreServices ? ` +${steps.length - 3} more` : ''}
            </Typography>
          </Tooltip>
        </Box>
      )}

      {/* Footer: Steps count + Estimated Cost */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 'auto', pt: 1 }}>
        <Tooltip title={`${steps.length} service${steps.length !== 1 ? 's' : ''} in this workflow`} arrow>
          <InfoChip bgcolor={`${THEME_COLOR}15`}>
            <FormatListBulletedIcon sx={{ fontSize: 12, color: THEME_COLOR }} />
            <span style={{ color: THEME_COLOR, fontWeight: 600 }}>{steps.length} steps</span>
          </InfoChip>
        </Tooltip>
        {costs.total > 0 && (
          <Tooltip title={`Govt: AED ${costs.govtCharge} + Service: AED ${costs.serviceCharge}`} arrow>
            <InfoChip bgcolor={`${THEME_COLOR}15`}>
              <AttachMoneyIcon sx={{ fontSize: 12, color: THEME_COLOR }} />
              <span style={{ color: THEME_COLOR, fontWeight: 600 }}>~AED {costs.total}</span>
            </InfoChip>
          </Tooltip>
        )}
      </Box>
    </TileContainer>
  );
};

export default WorkflowTile;
